import { BaseService } from './base';

export interface UploadBatch {
  id: string;
  store_id: string;
  organization_id: string;
  user_id: string;
  name: string;
  total_images: number;
  processed_images: number;
  failed_images: number;
  transformations: any[];
  transformation_credits: number;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'partially_completed' | 'failed';
  zip_url?: string;
  zip_expires_at?: string;
  error_message?: string;
  created_at: string;
  completed_at?: string;
}

// Review status type
export type ReviewStatus = 'none' | 'pending' | 'in_review' | 'approved' | 'rejected' | 'revision_requested';

// Transformation info
export interface AppliedTransformation {
  type: string;
  [key: string]: any;
}

export interface UploadedImage {
  id: string;
  upload_batch_id: string;
  original_filename: string;
  original_url: string;
  thumbnail_url?: string;
  processed_url?: string;
  cloudinary_public_id: string;
  status: 'uploaded' | 'processing' | 'completed' | 'failed';
  error_message?: string;
  file_size_bytes: number;
  width: number;
  height: number;
  format: string;
  image_type?: 'product' | 'detail' | 'barcode';
  created_at: string;
  processed_at?: string;
  // Review workflow fields
  review_status?: ReviewStatus;
  reviewer_id?: string;
  reviewed_at?: string;
  review_notes?: string;
  revision_count?: number;
  // Transformation info
  applied_transformations?: AppliedTransformation[];
  transformation_credits?: string;
}

export interface UploadResult {
  success: boolean;
  batchId: string;
  uploaded: Array<{
    id: string;
    filename: string;
    url: string;
    publicId: string;
    width: number;
    height: number;
    format: string;
    size: number;
  }>;
  errors: Array<{
    filename: string;
    error: string;
  }>;
  stats: {
    total: number;
    success: number;
    failed: number;
  };
}

export interface BatchStatus {
  id: string;
  name: string;
  status: string;
  progress: number;
  stats: {
    total: number;
    processed: number;
    failed: number;
    pending: number;
  };
  transformations: any[];
  creditsUsed: number;
  images: UploadedImage[];
  zipUrl?: string;
  zipExpiresAt?: string;
  createdAt: string;
  completedAt?: string;
}

export interface ProcessResult {
  success: boolean;
  batchId: string;
  status: string;
  imageCount: number;
  creditsRequired: number;
  transformations: string[];
  message: string;
}

export interface ZipDownloadResult {
  url: string;
  expiresAt: string;
  imageCount: number;
  cached: boolean;
}

export interface CreditCalculation {
  breakdown: Array<{
    type: string;
    credits_per_image: number;
    total_credits: number;
    description: string;
  }>;
  credits_per_image: number;
  total_credits: number;
  image_count: number;
  summary: {
    ai_transformations: number;
    basic_transformations: number;
  };
}

export interface CreditRatesResponse {
  rates: Record<string, number>;
  version: string;
}

export interface UploadProgress {
  stage: 'preparing' | 'uploading' | 'server_receiving' | 'cloudinary_upload' | 'finalizing' | 'complete' | 'error';
  percentage: number;
  bytesUploaded?: number;
  bytesTotal?: number;
  filesTotal?: number;
  filesProcessed?: number;
  currentFile?: string;
  message?: string;
  // Time tracking for more accurate estimates
  uploadStartTime?: number;
  estimatedTimeRemaining?: number;
}

export interface ProcessingStatus {
  taskId: string;
  status: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
  progress?: {
    batch_id?: string;
    total: number;
    processed: number;
    failed: number;
    progress: number;
    status: string;
  };
  result?: {
    status: string;
    batch_id: string;
    total: number;
    processed: number;
    failed: number;
    cancelled?: number;
    was_cancelled?: boolean;
    success_rate: number;
    total_time: number;
  };
  error?: string;
}

export interface BatchHistoryItem {
  id: string;
  store_id: string;
  organization_id: string;
  user_id: string;
  name: string;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'partially_completed' | 'failed';
  total_images: number;
  processed_images: number;
  failed_images: number;
  transformations: AppliedTransformation[];
  transformation_credits: number;
  celery_task_id?: string;
  error_message?: string;
  created_at: string;
  completed_at?: string;
  updated_at?: string;
  liveProgress?: ProcessingStatus | null;
}

export interface BatchHistoryResponse {
  data: BatchHistoryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface BatchHistoryFilters {
  page?: number;
  limit?: number;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface BatchHistoryDetail extends BatchHistoryItem {
  images: UploadedImage[];
}

export interface AssetFilters {
  page?: number;
  limit?: number;
  status?: string;
  reviewStatus?: string;
  batchId?: string;
  search?: string;
}

export interface AssetsResponse {
  assets: UploadedImage[];
  batches: Array<{ id: string; name: string; created_at: string }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UploadConnector {
  id: string;
  name: string;
  connector_type: string;
  organization_id: string;
  is_active: boolean;
  created_at: string;
}

export interface UploadProduct {
  id: string;
  organization_id: string;
  user_id: string;
  store_id: string;
  name_en?: string;
  name_ar?: string;
  description_en?: string;
  description_ar?: string;
  barcode?: string;
  sku?: string;
  price?: number;
  quantity?: number;
  product_type?: string;
  material?: string;
  brand?: string;
  size?: string;
  color?: string;
  status: 'draft' | 'partial' | 'complete';
  // AI Processing status
  processing_status?: 'idle' | 'processing' | 'completed' | 'failed';
  processing_started_at?: string;
  processing_completed_at?: string;
  // Retouch instructions for AI processing
  retouch_instructions?: string;
  // Dual approval fields
  media_approved: boolean;
  content_approved: boolean;
  media_approved_at?: string;
  content_approved_at?: string;
  media_approved_by?: string;
  content_approved_by?: string;
  // RetailOS integration
  retailos_pushed_at?: string;
  created_at: string;
  updated_at: string;
  image_count?: number;
  cover_image?: string;
  uploaded_images?: UploadedImage[];
  amazon_export?: {
    last_exported_at: string;
    export_count: number;
  } | null;
}

export interface ProductsResponse {
  products: UploadProduct[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateProductData {
  imageIds: string[];
  name_en?: string;
  name_ar?: string;
}

export interface UpdateProductData {
  name_en?: string;
  name_ar?: string;
  description_en?: string;
  description_ar?: string;
  barcode?: string;
  sku?: string;
  price?: string;
  quantity?: string;
  product_type?: string;
  material?: string;
  brand?: string;
  size?: string;
  color?: string;
  status?: 'draft' | 'partial' | 'complete';
  retouch_instructions?: string;
}

export interface ApprovalResult {
  id: string;
  media_approved: boolean;
  content_approved: boolean;
  media_approved_at?: string;
  content_approved_at?: string;
  media_approved_by?: string;
  content_approved_by?: string;
  status: 'draft' | 'partial' | 'complete';
  image_count: number;
}

export interface StudioRetouchResult {
  success: boolean;
  product: UploadProduct;
  barcode?: string;
  barcodeType?: string;
  title_en?: string;
  title_ar?: string;
  description_en?: string;
  description_ar?: string;
  processedImages: number;
  errors: string[];
}

export class UploadConnectorService extends BaseService {
  private readonly baseEndpoint = '/upload-connector';

  /**
   * Get or create the default upload connector for the organization
   */
  async getDefaultConnector(): Promise<UploadConnector> {
    return this.makeRequest<UploadConnector>(`${this.baseEndpoint}/default`);
  }

  /**
   * Get all assets for the organization with filters
   */
  async getAssets(filters: AssetFilters = {}): Promise<AssetsResponse> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);
    if (filters.reviewStatus) params.append('reviewStatus', filters.reviewStatus);
    if (filters.batchId) params.append('batchId', filters.batchId);
    if (filters.search) params.append('search', filters.search);

    const queryString = params.toString();
    return this.makeRequest<AssetsResponse>(
      `${this.baseEndpoint}/assets${queryString ? `?${queryString}` : ''}`
    );
  }

  // Chunked upload constants
  private readonly CHUNK_MAX_BYTES = 50 * 1024 * 1024; // 50MB per chunk
  private readonly CHUNK_MAX_FILES = 20;
  private readonly CHUNK_MAX_RETRIES = 2;

  /**
   * Upload images to an upload connector with chunked upload for reliability.
   *
   * Files are split into chunks (max 50MB / 20 files each) and uploaded
   * sequentially to the same batch. Each chunk is retried up to 2 times
   * on failure. Works reliably for any number of images (1 to 1000+).
   *
   * Progress stages:
   * - Uploading (0-90%): Real XHR progress across all chunks
   * - Finalizing (90-95%): Last chunk server processing
   * - Complete (100%): Done
   */
  async uploadImages(
    storeId: string,
    files: File[],
    batchName?: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    const chunks = this.splitFilesIntoChunks(files);
    const totalFiles = files.length;

    // Single chunk: use direct upload (no extra createBatch call)
    if (chunks.length === 1) {
      return this.uploadSingleChunk(storeId, chunks[0], totalFiles, {
        batchName,
        isLastChunk: true,
        onProgress,
      });
    }

    // Multi-chunk: create batch first, then upload each chunk
    const batch = await this.createBatch(storeId, batchName);
    const batchId = batch.id;

    const allUploaded: UploadResult['uploaded'] = [];
    const allErrors: UploadResult['errors'] = [];
    let filesCompletedSoFar = 0;

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const isLast = i === chunks.length - 1;
      const chunkFilesStart = filesCompletedSoFar;

      // Wrap onProgress to map chunk progress into overall progress.
      // Override stage to 'uploading' so the UI stage indicator doesn't
      // bounce between "uploading" and "processing" on each chunk.
      const chunkProgress = onProgress
        ? (progress: UploadProgress) => {
            const chunkWeight = chunk.length / totalFiles;
            const basePercent = (chunkFilesStart / totalFiles) * 90;
            const chunkContribution = (progress.percentage / 100) * chunkWeight * 90;
            const overallPercent = Math.round(basePercent + chunkContribution);

            onProgress({
              ...progress,
              stage: 'uploading',
              percentage: Math.min(90, overallPercent),
              filesTotal: totalFiles,
              filesProcessed: chunkFilesStart + (progress.filesProcessed || 0),
              message: `Uploading batch ${i + 1}/${chunks.length} — ${chunkFilesStart + (progress.filesProcessed || 0)} of ${totalFiles} images...`,
            });
          }
        : undefined;

      // Retry logic per chunk
      let lastError: Error | null = null;
      let chunkResult: UploadResult | null = null;

      for (let attempt = 0; attempt <= this.CHUNK_MAX_RETRIES; attempt++) {
        try {
          chunkResult = await this.uploadSingleChunk(storeId, chunk, chunk.length, {
            batchId,
            isLastChunk: isLast,
            onProgress: chunkProgress,
          });
          lastError = null;
          break;
        } catch (err: any) {
          lastError = err;
          if (attempt < this.CHUNK_MAX_RETRIES) {
            // Wait before retry (1s, then 2s)
            await new Promise(r => setTimeout(r, (attempt + 1) * 1000));
          }
        }
      }

      if (chunkResult) {
        allUploaded.push(...chunkResult.uploaded);
        allErrors.push(...chunkResult.errors);
        filesCompletedSoFar += chunk.length;
      } else {
        // Chunk failed after all retries — record all files as errors
        for (const file of chunk) {
          allErrors.push({
            filename: file.name,
            error: lastError?.message || 'Upload failed after retries',
          });
        }
        filesCompletedSoFar += chunk.length;
      }
    }

    // Final progress
    if (onProgress) {
      onProgress({
        stage: 'finalizing',
        percentage: 95,
        filesTotal: totalFiles,
        filesProcessed: allUploaded.length,
        message: 'Finalizing...',
      });
    }

    return {
      success: allUploaded.length > 0,
      batchId,
      uploaded: allUploaded,
      errors: allErrors,
      stats: {
        total: totalFiles,
        success: allUploaded.length,
        failed: allErrors.length,
      },
    };
  }

  /**
   * Split files into chunks based on total size and file count limits.
   */
  private splitFilesIntoChunks(files: File[]): File[][] {
    const chunks: File[][] = [];
    let currentChunk: File[] = [];
    let currentSize = 0;

    for (const file of files) {
      if (
        currentChunk.length > 0 &&
        (currentSize + file.size > this.CHUNK_MAX_BYTES ||
          currentChunk.length >= this.CHUNK_MAX_FILES)
      ) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentSize = 0;
      }

      currentChunk.push(file);
      currentSize += file.size;
    }

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }

    return chunks;
  }

  /**
   * Upload a single chunk of files via XHR with progress tracking.
   */
  private uploadSingleChunk(
    storeId: string,
    files: File[],
    totalFilesForProgress: number,
    options: {
      batchId?: string;
      batchName?: string;
      isLastChunk?: boolean;
      onProgress?: (progress: UploadProgress) => void;
    }
  ): Promise<UploadResult> {
    const { batchId, batchName, isLastChunk, onProgress } = options;

    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));
    if (batchId) formData.append('batchId', batchId);
    if (batchName) formData.append('batchName', batchName);
    if (isLastChunk) formData.append('isLastChunk', 'true');

    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
    const avgFileSize = totalBytes / files.length;
    const estimatedTimePerFile = Math.min(3000, 1500 + (avgFileSize / (5 * 1024 * 1024)) * 1500);
    const estimatedProcessingTime = files.length * estimatedTimePerFile;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let uploadComplete = false;
      const uploadStartTime = Date.now();
      let processingStartTime: number | null = null;

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const uploadProgress = event.loaded / event.total;
          const percentage = Math.round(uploadProgress * 50);

          const elapsed = Date.now() - uploadStartTime;
          const bytesPerMs = event.loaded / Math.max(elapsed, 1);
          const remainingBytes = event.total - event.loaded;
          const remainingUploadTime = remainingBytes / bytesPerMs;
          const estimatedRemaining = remainingUploadTime + estimatedProcessingTime;

          onProgress({
            stage: 'uploading',
            percentage,
            bytesUploaded: event.loaded,
            bytesTotal: event.total,
            filesTotal: totalFilesForProgress,
            uploadStartTime,
            estimatedTimeRemaining: Math.round(estimatedRemaining),
            message: `Uploading ${files.length} ${files.length === 1 ? 'image' : 'images'}...`,
          });

          if (event.loaded === event.total && !uploadComplete) {
            uploadComplete = true;
            processingStartTime = Date.now();

            onProgress({
              stage: 'server_receiving',
              percentage: 52,
              filesTotal: totalFilesForProgress,
              estimatedTimeRemaining: Math.round(estimatedProcessingTime),
              message: 'Server receiving files...',
            });

            let currentProgress = 55;
            const progressPerFile = 35 / files.length;
            let simulatedFilesProcessed = 0;

            const processingInterval = setInterval(() => {
              const elapsedProcessing = Date.now() - (processingStartTime || Date.now());
              const expectedFilesProcessed = Math.min(
                files.length,
                Math.floor(elapsedProcessing / estimatedTimePerFile)
              );

              if (expectedFilesProcessed > simulatedFilesProcessed) {
                simulatedFilesProcessed = expectedFilesProcessed;
                currentProgress = Math.min(90, 55 + simulatedFilesProcessed * progressPerFile);

                onProgress({
                  stage: 'cloudinary_upload',
                  percentage: Math.round(currentProgress),
                  filesTotal: totalFilesForProgress,
                  filesProcessed: simulatedFilesProcessed,
                  estimatedTimeRemaining: Math.round((files.length - simulatedFilesProcessed) * estimatedTimePerFile),
                  message: `Processing ${simulatedFilesProcessed} of ${files.length} images...`,
                });
              } else if (currentProgress < 90 && elapsedProcessing > estimatedProcessingTime * 0.9) {
                currentProgress = Math.min(90, currentProgress + 0.5);
                onProgress({
                  stage: 'cloudinary_upload',
                  percentage: Math.round(currentProgress),
                  filesTotal: totalFilesForProgress,
                  filesProcessed: simulatedFilesProcessed,
                  message: 'Processing images (this may take a moment)...',
                });
              }
            }, 500);

            (xhr as any)._processingInterval = processingInterval;
          }
        }
      });

      xhr.addEventListener('load', () => {
        if ((xhr as any)._processingInterval) {
          clearInterval((xhr as any)._processingInterval);
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          if (onProgress) {
            onProgress({
              stage: 'finalizing',
              percentage: 95,
              filesTotal: totalFilesForProgress,
              filesProcessed: files.length,
              message: 'Finalizing...',
            });
          }

          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response.data || response);
          } catch {
            reject(new Error('Invalid response from server'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.message || 'Failed to upload images'));
          } catch {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        if ((xhr as any)._processingInterval) {
          clearInterval((xhr as any)._processingInterval);
        }
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        if ((xhr as any)._processingInterval) {
          clearInterval((xhr as any)._processingInterval);
        }
        reject(new Error('Upload was cancelled'));
      });

      xhr.addEventListener('timeout', () => {
        if ((xhr as any)._processingInterval) {
          clearInterval((xhr as any)._processingInterval);
        }
        reject(new Error('Upload timed out. Try uploading fewer images at once.'));
      });

      xhr.open('POST', `${this.getApiUrl()}${this.baseEndpoint}/${storeId}/upload`);

      // Scale timeout with file count: 2min base + 6s per file, max 10min
      xhr.timeout = Math.min(600000, 120000 + files.length * 6000);

      const token = localStorage.getItem('access_token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.send(formData);
    });
  }

  /**
   * Create a new upload batch
   */
  async createBatch(storeId: string, name?: string): Promise<UploadBatch> {
    return this.makeRequest<UploadBatch>(`${this.baseEndpoint}/${storeId}/batches`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  /**
   * Get all batches for a store
   */
  async getBatches(storeId: string, page = 1, limit = 20): Promise<{ batches: UploadBatch[]; pagination: any }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/batches?page=${page}&limit=${limit}`);
  }

  /**
   * Get batch details and status
   */
  async getBatchStatus(storeId: string, batchId: string): Promise<BatchStatus> {
    return this.makeRequest<BatchStatus>(`${this.baseEndpoint}/${storeId}/batches/${batchId}`);
  }

  /**
   * Get quick batch status (for polling)
   */
  async getBatchStatusQuick(storeId: string, batchId: string): Promise<{ id: string; status: string; progress: number; stats: any }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/batches/${batchId}/status`);
  }

  /**
   * Start processing a batch with transformations
   */
  async processBatch(storeId: string, batchId: string, transformations: any[]): Promise<ProcessResult> {
    return this.makeRequest<ProcessResult>(`${this.baseEndpoint}/${storeId}/batches/${batchId}/process`, {
      method: 'POST',
      body: JSON.stringify({ transformations }),
    });
  }

  /**
   * Get download URL for a single image
   */
  async getImageDownloadUrl(storeId: string, imageId: string): Promise<{ id: string; filename: string; url: string; isProcessed: boolean }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/images/${imageId}/download`);
  }

  /**
   * Generate ZIP download for batch
   */
  async generateZipDownload(storeId: string, batchId: string): Promise<ZipDownloadResult> {
    return this.makeRequest<ZipDownloadResult>(`${this.baseEndpoint}/${storeId}/batches/${batchId}/download-zip`, {
      method: 'POST',
    });
  }

  /**
   * Generate ZIP download for selected images (by IDs)
   */
  async downloadSelectedImages(storeId: string, imageIds: string[]): Promise<ZipDownloadResult> {
    return this.makeRequest<ZipDownloadResult>(`${this.baseEndpoint}/${storeId}/images/download-zip`, {
      method: 'POST',
      body: JSON.stringify({ imageIds }),
    });
  }

  /**
   * Generate ZIP download for all images matching filters
   */
  async downloadByFilters(storeId: string, filters: Partial<AssetFilters>): Promise<ZipDownloadResult> {
    return this.makeRequest<ZipDownloadResult>(`${this.baseEndpoint}/${storeId}/images/download-zip`, {
      method: 'POST',
      body: JSON.stringify({ filters }),
    });
  }

  /**
   * Delete a batch and all its images
   */
  async deleteBatch(storeId: string, batchId: string): Promise<{ success: boolean; batchId: string; imagesDeleted: number }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/batches/${batchId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Delete a single image
   */
  async deleteImage(storeId: string, imageId: string): Promise<{ success: boolean; imageId: string; batchDeleted?: string | null }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Delete multiple images
   */
  async deleteImages(storeId: string, imageIds: string[]): Promise<{ success: boolean; deletedCount: number; imageIds: string[]; deletedBatches?: string[] }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/images/delete-bulk`, {
      method: 'POST',
      body: JSON.stringify({ imageIds }),
    });
  }

  /**
   * Revert a processed image back to its original state
   */
  async revertImage(storeId: string, imageId: string): Promise<{ success: boolean; image: UploadedImage }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/images/${imageId}/revert`, {
      method: 'POST',
    });
  }

  /**
   * Apply Studio Retouch to a single image
   * @param storeId - Store ID
   * @param imageId - Image ID
   * @param instructions - Optional retouch instructions for the AI
   */
  async studioRetouchSingleImage(
    storeId: string,
    imageId: string,
    instructions?: string
  ): Promise<{ success: boolean; image: UploadedImage; processingTime?: number; imageType?: string }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/images/${imageId}/studio-retouch`, {
      method: 'POST',
      body: JSON.stringify({ instructions }),
    });
  }

  /**
   * Calculate credits for transformations
   */
  async calculateCredits(storeId: string, transformations: any[], imageCount = 1): Promise<CreditCalculation> {
    return this.makeRequest<CreditCalculation>(`${this.baseEndpoint}/${storeId}/images/calculate-credits`, {
      method: 'POST',
      body: JSON.stringify({ transformations, imageCount }),
    });
  }

  /**
   * Get credit rates from the centralized configuration
   */
  async getCreditRates(): Promise<CreditRatesResponse> {
    return this.makeRequest<CreditRatesResponse>(`${this.baseEndpoint}/credit-rates`);
  }

  // ============================================
  // PRODUCT METHODS
  // ============================================

  /**
   * Create a new product from selected images
   */
  async createProduct(storeId: string, data: CreateProductData): Promise<UploadProduct> {
    return this.makeRequest<UploadProduct>(`${this.baseEndpoint}/${storeId}/products`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get all products for a store
   */
  async getProducts(storeId: string, page = 1, limit = 20, status?: string): Promise<ProductsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (status) params.append('status', status);

    return this.makeRequest<ProductsResponse>(
      `${this.baseEndpoint}/${storeId}/products?${params.toString()}`
    );
  }

  /**
   * Get a single product with its images
   */
  async getProduct(storeId: string, productId: string): Promise<UploadProduct> {
    return this.makeRequest<UploadProduct>(
      `${this.baseEndpoint}/${storeId}/products/${productId}`
    );
  }

  /**
   * Update a product
   */
  async updateProduct(storeId: string, productId: string, data: UpdateProductData): Promise<UploadProduct> {
    return this.makeRequest<UploadProduct>(
      `${this.baseEndpoint}/${storeId}/products/${productId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Delete a product (images are unlinked, not deleted)
   */
  async deleteProduct(storeId: string, productId: string): Promise<{ success: boolean; productId: string }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/products/${productId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Add images to a product
   */
  async addImagesToProduct(
    storeId: string,
    productId: string,
    imageIds: string[]
  ): Promise<{ success: boolean; addedCount: number }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/products/${productId}/images`, {
      method: 'POST',
      body: JSON.stringify({ imageIds }),
    });
  }

  /**
   * Remove images from a product
   */
  async removeImagesFromProduct(
    storeId: string,
    productId: string,
    imageIds: string[]
  ): Promise<{ success: boolean; removedCount: number }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/products/${productId}/images`, {
      method: 'DELETE',
      body: JSON.stringify({ imageIds }),
    });
  }

  /**
   * Approve media for a product (Graphic Team)
   */
  async approveMedia(
    storeId: string,
    productId: string,
    approved: boolean = true
  ): Promise<UploadProduct> {
    return this.makeRequest<UploadProduct>(
      `${this.baseEndpoint}/${storeId}/products/${productId}/approve-media`,
      {
        method: 'POST',
        body: JSON.stringify({ approved }),
      }
    );
  }

  /**
   * Approve content for a product (Content Team)
   */
  async approveContent(
    storeId: string,
    productId: string,
    approved: boolean = true
  ): Promise<UploadProduct> {
    return this.makeRequest<UploadProduct>(
      `${this.baseEndpoint}/${storeId}/products/${productId}/approve-content`,
      {
        method: 'POST',
        body: JSON.stringify({ approved }),
      }
    );
  }

  // ============================================
  // TRANSFORMATION METHODS
  // ============================================

  /**
   * Get transformation presets for the user's plan
   */
  async getTransformationPresets(): Promise<{ presets: Record<string, any>; plan: string }> {
    return this.makeRequest(`${this.baseEndpoint}/presets`);
  }

  /**
   * Process selected images with transformations
   */
  async processImages(
    storeId: string,
    imageIds: string[],
    transformations: any[]
  ): Promise<{
    success: boolean;
    processingBatchId: string;
    status: string;
    imageCount: number;
    creditsRequired: number;
    transformations: string[];
    processingMethod?: string;
    taskId?: string;
    estimatedTime?: number;
    message: string;
  }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/images/process`, {
      method: 'POST',
      body: JSON.stringify({ imageIds, transformations }),
    });
  }

  /**
   * Get processing task status (for real-time progress tracking)
   */
  async getProcessingStatus(taskId: string): Promise<ProcessingStatus> {
    return this.makeRequest(`${this.baseEndpoint}/processing-status/${taskId}`);
  }

  /**
   * Cancel a running processing task
   */
  async cancelProcessing(taskId: string): Promise<{ success: boolean; message: string; batch_id?: string }> {
    return this.makeRequest(`${this.baseEndpoint}/cancel-processing/${taskId}`, {
      method: 'POST',
    });
  }

  // ============================================
  // STUDIO RETOUCH METHODS
  // ============================================

  /**
   * Update image type (product, detail, or barcode)
   */
  async updateImageType(
    storeId: string,
    imageId: string,
    imageType: 'product' | 'detail' | 'barcode'
  ): Promise<{ success: boolean; image: UploadedImage }> {
    return this.makeRequest(`${this.baseEndpoint}/${storeId}/images/${imageId}/type`, {
      method: 'PATCH',
      body: JSON.stringify({ imageType }),
    });
  }

  /**
   * Apply Studio Retouch workflow to a product
   * - Reads barcode from barcode image
   * - Applies full retouch transformations to product images
   * - Generates title and description in EN/AR
   */
  async studioRetouch(
    storeId: string,
    productId: string
  ): Promise<StudioRetouchResult> {
    return this.makeRequest<StudioRetouchResult>(
      `${this.baseEndpoint}/${storeId}/products/${productId}/studio-retouch`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Push product to RetailOS
   * Requires both media and content approval
   */
  async pushToRetailOS(
    storeId: string,
    productId: string
  ): Promise<{ success: boolean; message: string; retailos_response?: any; pushed_at: string }> {
    return this.makeRequest(
      `${this.baseEndpoint}/${storeId}/products/${productId}/push-retailos`,
      {
        method: 'POST',
      }
    );
  }

  // ============================================
  // BATCH HISTORY METHODS
  // ============================================

  /**
   * Get batch history with filtering and pagination
   */
  async getBatchHistory(filters: BatchHistoryFilters = {}): Promise<BatchHistoryResponse> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);

    const queryString = params.toString();
    return this.makeRequest<BatchHistoryResponse>(
      `${this.baseEndpoint}/batch-history${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Get detailed batch information with all images
   */
  async getBatchDetail(batchId: string, imageStatus?: string): Promise<BatchHistoryDetail> {
    const params = new URLSearchParams();
    if (imageStatus) params.append('imageStatus', imageStatus);

    const queryString = params.toString();
    return this.makeRequest<BatchHistoryDetail>(
      `${this.baseEndpoint}/batch-history/${batchId}${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Cancel a running batch via batch ID (uses stored celery_task_id)
   */
  async cancelBatch(batchId: string): Promise<{ success: boolean; message: string }> {
    return this.makeRequest<{ success: boolean; message: string }>(
      `${this.baseEndpoint}/batch-history/${batchId}/cancel`,
      { method: 'POST' }
    );
  }

  /**
   * Helper to get API URL
   */
  private getApiUrl(): string {
    return (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';
  }
}

export const uploadConnectorService = new UploadConnectorService();
